import React, { useCallback, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  Shield,
  Lock,
  MapPin,
} from "lucide-react";
import { TralenexLogo } from "@/components/brand/TralenexLogo";
import { useAuth } from "@/contexts/AuthContext";
import { useTenantData } from "@/contexts/TenantModeContext";
import SessionTimer from "./SessionTimer";
import EnvironmentSwitcher from "./EnvironmentSwitcher";
import NotificationsMenu from "./NotificationsMenu";
import RoleSwitcher from "./RoleSwitcher";
import UserMenu from "./UserMenu";
import GlobalSearch from "./GlobalSearch";
import DemoControlPanel from "./DemoControlPanel";
import TimeTravelPill from "./TimeTravelPill";

export default function Header() {
  const { user, signOut, switchRole } = useAuth();
  const { tenant, isDemo, resetDemo } = useTenantData();
  const navigate = useNavigate();
  const location = useLocation();
  // Time-travel only shown on the home dashboard for CEO + CAIO personas.
  const onHome = location.pathname === "/dashboard";
  const showTimeTravel = onHome && (user?.roleCode === "CEO" || user?.roleCode === "CAIO");

  const [openMenu, setOpenMenu] = useState(null); // 'user' | 'role' | 'notifications' | null

  const toggle = useCallback(
    (id) => () => setOpenMenu((current) => (current === id ? null : id)),
    [],
  );

  const handleSignOut = useCallback(() => {
    signOut();
    navigate("/sign-in", { replace: true });
  }, [signOut, navigate]);

  const handleRoleSwitch = useCallback(
    (id) => {
      switchRole(id);
      setOpenMenu(null);
    },
    [switchRole],
  );

  return (
    <header
      className="h-14 sticky top-0 z-30 bg-raised border-b border-border-subtle"
      data-testid="app-header"
    >
      <div className="h-full px-4 flex items-center gap-3">
        {/* Brand lock-up */}
        <Link
          to="/dashboard"
          className="hidden md:flex items-center gap-2.5 shrink-0"
          data-testid="header-brand-lockup"
        >
          <TralenexLogo height={30} />
          <span className="text-meta font-semibold tracking-[0.08em] uppercase text-ink-secondary">
            · ALGOS
          </span>
        </Link>

        <div className="h-5 w-px bg-border-subtle hidden md:block" />

        {/* Tenant context (read-only breadcrumb) */}
        <span
          className="inline-flex items-center gap-1.5 text-meta text-ink-secondary max-w-[280px]"
          data-testid="header-tenant-context"
        >
          <span className="font-semibold text-ink-primary truncate">{tenant.name}</span>
        </span>

        {/* Persona role badge — RBAC affordance */}
        {user && (
          <span
            className="hidden md:inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-bold tracking-[0.1em] uppercase bg-tralenex-50 text-tralenex-700 border border-tralenex-300"
            data-testid="header-role-badge"
            title={user.role}
          >
            {user.roleCode}
          </span>
        )}

        {/* Global search */}
        <GlobalSearch />

        {/* Session timer */}
        <div className="hidden lg:flex items-center">
          <SessionTimer />
        </div>

        {/* Residency / encryption / compliance chips */}
        <ComplianceChips region={tenant.region} />

        {/* Time-travel — only on CEO/CAIO home */}
        {showTimeTravel && <TimeTravelPill />}

        {/* Demo-only persona switcher */}
        {isDemo && (
          <RoleSwitcher
            open={openMenu === "role"}
            onToggle={toggle("role")}
            currentId={user?.id}
            onSwitch={handleRoleSwitch}
          />
        )}

        <NotificationsMenu
          open={openMenu === "notifications"}
          onToggle={toggle("notifications")}
          isDemo={isDemo}
          showDot={isDemo}
          tenantId={tenant?.id}
        />

        {/* Environment switcher — primary tenant control */}
        <EnvironmentSwitcher />

        <UserMenu
          user={user}
          open={openMenu === "user"}
          onToggle={toggle("user")}
          onSignOut={handleSignOut}
        />
      </div>
      <DemoControlPanel isDemo={isDemo} user={user} onResetSeed={resetDemo} />
    </header>
  );
}

function ComplianceChips({ region }) {
  return (
    <div className="hidden xl:flex items-center gap-1.5 text-meta text-ink-secondary">
      <span className="inline-flex items-center gap-1 px-2 py-1 rounded bg-inset">
        <MapPin size={11} /> {region}
      </span>
      <span className="inline-flex items-center gap-1 px-2 py-1 rounded bg-inset">
        <Lock size={11} /> TLS 1.3
      </span>
      <span className="inline-flex items-center gap-1 px-2 py-1 rounded bg-inset">
        <Shield size={11} /> APRA
      </span>
    </div>
  );
}
